import * as webMercatorUtils from '@arcgis/core/geometry/support/webMercatorUtils.js';
import { ESRI, GOOGLE } from '@rsos/constants/mapProviders';
import AlertsLayer from './RSOSLayers/AlertsLayer';
import CallsLayer from './RSOSLayers/CallsLayer';
import IconPolygonLayer, {
  IconPolygonLayerReversed,
} from './RSOSLayers/IconPolygonLayer';
import TrailingPathLayer, {
  TrailingPathLayerReversed,
} from './RSOSLayers/TrailingPathLayer';
import W3WLayer from './RSOSLayers/W3WLayer';
import { getGmapsViewState, normalizeZoomLevel } from './utils';

let gMapsEventListeners = [];

const defaultCursorForProvider = mapProvider =>
  mapProvider === ESRI ? 'default' : 'grab';

class DeckOverlay {
  constructor(mapProvider, overlay) {
    this._cursor = defaultCursorForProvider(mapProvider);
    this._mapInstance = null;
    this._onViewStateChange = () => {};

    this.mapProvider = mapProvider;
    this.overlay = overlay;

    this._getCursor(() => this._cursor);
  }

  /**
   * An internal method that registers DeckOverlay.getCursor which is called
   * continuously by DeckGL to get the current cursor value. See constructor().
   * Note: Google maps doesn't invoke this function, so we need to explicitly
   * set the cursor style in the setCursor...() functions below
   * @param {Function} cb - A function that returns a valid cursor value.
   */
  _getCursor(cb = () => {}) {
    const mapProvider = this.mapProvider;
    if (mapProvider === ESRI) {
      this.overlay.deck.getCursor = cb;
    } else if (mapProvider === GOOGLE) {
      this.overlay.getCursor = cb;
    }
  }

  /**
   * Changes the cursor to a pointer.
   */
  setCursorPointer() {
    const mapProvider = this.mapProvider;
    this._cursor = 'pointer';
    if (mapProvider === GOOGLE) {
      this._mapInstance.setOptions({ draggableCursor: 'pointer' });
    }
  }

  /**
   * Changes the cursor to the default.
   */
  setCursorDefault() {
    const mapProvider = this.mapProvider;
    this._cursor = defaultCursorForProvider(mapProvider);
    if (mapProvider === GOOGLE) {
      this._mapInstance.setOptions({ draggableCursor: 'grab' });
    }
  }

  /**
   * Registers a callback for when the user clicks on the overlay.
   * @param {Function} cb - an onClick handler
   */
  onClick(cb = () => {}) {
    const mapProvider = this.mapProvider;
    if (mapProvider === ESRI) {
      this.overlay.deck.onClick = cb;
    } else if (mapProvider === GOOGLE) {
      this.overlay.onClick = cb;
    }
  }

  /**
   * Register a callback to be called when the view state changes.
   * The callback will be invoked with viewState details as the only argument.
   * @param {Function} cb - A callback function that will be called when the
   * view state changes.
   */
  onViewStateChange(cb = () => {}) {
    this._onViewStateChange = cb;
  }

  /**
   * Called internally when the map is set on the instance. This allows the
   * caller to register a callback asynchronously and possibly before the map
   * instance is set. Since this depends directly on the map provider's instance
   * it's binding to the mapInstance when `setMap` is called.
   */
  _bindOnViewStateChange(mapInstance) {
    const mapProvider = this.mapProvider;
    if (mapProvider === ESRI) {
      if (mapInstance?.view?.watch) {
        const _this = this;
        // Use ESRI's stationary property - only fire callback when view stops moving
        mapInstance.view.watch('stationary', function (isStationary) {
          if (!isStationary) return;
          const viewState = {
            longitude: this.center?.x,
            latitude: this.center?.y,
            width: this.width,
            height: this.height,
            zoom: this.zoom,
          };
          // Convert the center from meters to degrees.
          const [longitude, latitude] = webMercatorUtils.xyToLngLat(
            viewState.longitude,
            viewState.latitude,
          );
          viewState.longitude = longitude;
          viewState.latitude = latitude;
          _this._onViewStateChange(viewState);
        });
      }
    } else if (mapProvider === GOOGLE) {
      const _this = this;

      const gMapsHandleEventListener = () => {
        const viewState = getGmapsViewState(mapInstance);

        _this._onViewStateChange(viewState);
      };

      const events = ['center_changed', 'zoom_changed', 'mouseover'];
      for (let index = 0; index < events.length; index++) {
        const event = events[index];
        // Remove any existing listeners since _bindOnViewStateChange() is
        // expected to be called multiple times per session
        if (!window.google?.maps) {
          // eslint-disable-next-line no-console -- deliberately want more detailed error information in sentry here
          console.error(
            'Google Maps JavaScript API is not loaded. gMaps event listeners cannot be removed or added.',
          );
          return;
        }
        window.google.maps.event.removeListener(gMapsEventListeners[index]);

        gMapsEventListeners.push(
          window.google.maps.event.addListener(mapInstance, event, () => {
            gMapsHandleEventListener();
          }),
        );
      }
    }
  }

  getViewState() {
    const mapProvider = this.mapProvider;
    if (mapProvider === ESRI) {
      if (this._mapInstance) {
        const { view } = this._mapInstance;

        const [longitude, latitude] = webMercatorUtils.xyToLngLat(
          view.center?.x,
          view.center?.y,
        );
        const viewState = {
          longitude,
          latitude,
          width: view.width,
          height: view.height,
          zoom: normalizeZoomLevel(this._mapInstance, ESRI),
        };
        return viewState;
      }
    } else {
      if (this._mapInstance) {
        return getGmapsViewState(this._mapInstance, GOOGLE);
      }
    }
  }

  /**
   * Sets the map instance for the overlay. To be set when the map is ready.
   * @param {Object} - The map instance from the provider (e.g. ESRI or Google).
   */
  setMap(mapInstance) {
    this._mapInstance = mapInstance;

    const mapProvider = this.mapProvider;
    if (mapProvider === ESRI) {
      mapInstance.map.layers.add(this.overlay);
    } else if (mapProvider === GOOGLE) {
      this.overlay.setMap(mapInstance);
    }

    this._bindOnViewStateChange(mapInstance);
  }

  /**
   * Sets the layer props.
   * @param  {...any} props
   */
  setProps(...props) {
    if (this._destroyed) return;
    const mapProvider = this.mapProvider;
    if (mapProvider === ESRI) {
      // Guard against null deck during map-provider transitions.
      // The ESRI DeckLayer's internal deck instance can be destroyed before
      // all pending setLayers() calls finish.
      if (this.overlay?.deck) {
        this.overlay.deck.set(...props);
      }
    } else if (mapProvider === GOOGLE) {
      this.overlay.setProps(...props);
    }
  }

  /**
   * A convenience helper that calls setProps with the given layers.
   * @param {Array} layers
   */
  setLayers(layers = []) {
    let layersOrder = layers;
    // Reverse the order of the layers for Google Maps.
    // This should also include the subLayers of each layer.
    if (this.mapProvider === GOOGLE) {
      layersOrder = layers
        .map(layer => {
          if (layer instanceof TrailingPathLayer) {
            return new TrailingPathLayerReversed(layer.props);
          } else if (layer instanceof IconPolygonLayer) {
            return new IconPolygonLayerReversed(layer.props);
          } else if (layer instanceof CallsLayer) {
            // return new CallsLayerReversed(layer.props);
          } else if (layer instanceof AlertsLayer) {
            // return new AlertsLayer(layer.props);
          } else if (layer instanceof W3WLayer) {
            // return new W3WLayer(layer.props);
          }
          return layer;
        })
        .reverse();
    }

    this.setProps({ layers: layersOrder });
  }
}

export default DeckOverlay;
